import { keepPreviousData, queryOptions, useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { CommonError } from 'shared/api/types';
import { handleRequestError } from 'shared/lib/axios';
import { queryClient } from 'shared/lib/react-query';
import {
  approveTimesheetStatus,
  closeTimesheetPeriodState,
  disapproveTimesheetStatus,
  getTimesheets,
  openTimesheetPeriodState,
  updateTimesheetActualHours,
} from './request';
import { TimesheetAccount, TimesheetAccountListRequest, TimesheetActualHoursUpdateRequest } from './types';

export const keys = {
  root: () => ['timesheet'] as const,
  list: () => [...keys.root(), 'list'] as const,
  listWithFilters: (filters: TimesheetAccountListRequest) => [...keys.root(), 'list', filters] as const,
  openTimesheetPeriodState: (id: string) => [...keys.root(), 'openTimesheetPeriodState', id] as const,
  closeTimesheetPeriodState: (id: string) => [...keys.root(), 'closeTimesheetPeriodState', id] as const,
  approveTimesheetStatus: (id: string) => [...keys.root(), 'approveTimesheetStatus', id] as const,
  disapproveTimesheetStatus: (id: string) => [...keys.root(), 'disapproveTimesheetStatus', id] as const,
  updateTimesheetActualHours: (id: string) => [...keys.root(), 'updateTimesheetActualHours', id] as const,
};

export const timesheetsService = {
  queryKey: (filters: TimesheetAccountListRequest) => keys.listWithFilters(filters),

  getCache: (filters: TimesheetAccountListRequest) =>
    queryClient.getQueryData<TimesheetAccount[]>(timesheetsService.queryKey(filters)),

  queryOptions: (filters: TimesheetAccountListRequest) => {
    const timesheetsKey = timesheetsService.queryKey(filters);
    return queryOptions({
      queryKey: timesheetsKey,
      queryFn: async () => {
        const timesheets = await getTimesheets(filters);
        return timesheets.content[0]?.accounts || [];
      },
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      initialData: () => timesheetsService.getCache(filters)!,
      initialDataUpdatedAt: () => queryClient.getQueryState(timesheetsKey)?.dataUpdatedAt,
      placeholderData: keepPreviousData,
    });
  },
  prefetchQuery: async (filters: TimesheetAccountListRequest) =>
    queryClient.prefetchQuery(timesheetsService.queryOptions(filters)),
};

export const useOpenTimesheetPeriodStateMutation = (id: string): UseMutationResult<void, CommonError, void> => {
  const qClient = useQueryClient();

  const updateKey = keys.openTimesheetPeriodState(id);

  return useMutation({
    mutationKey: updateKey,
    mutationFn: () => openTimesheetPeriodState(id),
    onSuccess: () => {
      qClient.invalidateQueries({ queryKey: keys.list() });
    },
    onError: (error) => {
      handleRequestError(error);
    },
  });
};

export const useCloseTimesheetPeriodStateMutation = (id: string): UseMutationResult<void, CommonError, void> => {
  const qClient = useQueryClient();

  const updateKey = keys.closeTimesheetPeriodState(id);

  return useMutation({
    mutationKey: updateKey,
    mutationFn: () => closeTimesheetPeriodState(id),
    onSuccess: () => {
      qClient.invalidateQueries({ queryKey: keys.list() });
    },
    onError: (error) => {
      handleRequestError(error);
    },
  });
};

export const useApproveTimesheetStatusMutation = (id: string): UseMutationResult<void, CommonError, void> => {
  const qClient = useQueryClient();

  const updateKey = keys.approveTimesheetStatus(id);

  return useMutation({
    mutationKey: updateKey,
    mutationFn: () => approveTimesheetStatus(id),
    onSuccess: () => {
      qClient.invalidateQueries({ queryKey: keys.list() });
    },
    onError: (error) => {
      handleRequestError(error);
    },
  });
};

export const useDisapproveTimesheetStatusMutation = (id: string): UseMutationResult<void, CommonError, void> => {
  const qClient = useQueryClient();

  const updateKey = keys.disapproveTimesheetStatus(id);

  return useMutation({
    mutationKey: updateKey,
    mutationFn: () => disapproveTimesheetStatus(id),
    onSuccess: () => {
      qClient.invalidateQueries({ queryKey: keys.list() });
    },
    onError: (error) => {
      handleRequestError(error);
    },
  });
};

export const useUpdateTimesheetActualHoursMutation = (
  id: string,
): UseMutationResult<TimesheetAccount, CommonError, TimesheetActualHoursUpdateRequest> => {
  const qClient = useQueryClient();

  const updateKey = keys.updateTimesheetActualHours(id);

  return useMutation({
    mutationKey: updateKey,
    mutationFn: (payload) => updateTimesheetActualHours(payload),
    onSuccess: (data) => {
      qClient.invalidateQueries({ queryKey: keys.list() });
      return data;
    },
    onError: (error) => {
      handleRequestError(error);
    },
  });
};
